import React, { useState } from "react";

import { ColumnType } from "antd/es/table";

import { formatNumber, trimToDecimals, useTranslation } from "utils";

import { FOOTPRINTING_CATEGORY_COLORS } from "consts";

import { FootprintCalculationSummary, FootprintingCategory, FootprintingResultsDataTableRow, SubDataRowSource } from "models";

import { Flex, Icon, Table, TooltipWrapper } from "components";

interface FootprintingResultTableProps {
  footprintCalculationSummary?: FootprintCalculationSummary;
  hidePerYield?: boolean;
}

interface TableDataRow {
  key: React.Key;
  scope: string;
  source: React.Key;
  dot: React.ReactNode | null;
  expandable: boolean;
  totalCO2e: string;
  perHaCO2e: string;
  perKgYieldCO2e: string;
  shareOfTotal: string;
  expandIcon?: React.ReactNode;
}

interface ColumnConfiguration extends ColumnType {
  title: React.ReactNode | string | null;
  dataIndex: keyof TableDataRow;
}

type CalculationSummaryTuple = [FootprintingCategory, FootprintingResultsDataTableRow];

// Category and subcategory scope mappings (Scope 1-3)
export const FOOTPRINTING_SCOPE_MAPPING: Record<FootprintingCategory, Partial<Record<SubDataRowSource, number>> | number> = {
  energy: {
    diesel: 1,
    dieselDrying: 1,
    benzin: 1,
    benzinDrying: 1,
    naturalGas: 1,
    naturalGasDrying: 1,
    heating: 1,
    heatingDrying: 1,
    electricity: 2,
    electricityDrying: 2,
    renewableElectricityDrying: 2,
    woodchipHeaterDrying: 1,
  },
  fertilizer: {
    mineralFertilizers: 3, // Production scope (emissions on field would be scope 1, but appears as separate entry if exists)
    organicFertilizers: 3, // Production scope
    lime: 3, // Production scope
  },
  fieldWork: {
    harvesting: 1,
    fertilizerApplication: 1,
    plantProtectionApplications: 1,
    seedingApplication: 1,
    tillageApplication: 1,
  },
  transportation: {
    upstreamTransportation: 3,
    downstreamTransportation: 3,
  },
  other: {
    water: 3,
  },
  plantProtection: 1,
  luc: 1,
  residue: 1,
  refrigerants: 1,
  waste: 3,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const FootprintingResultTable: React.FC<FootprintingResultTableProps> = ({ footprintCalculationSummary, hidePerYield = false }) => {
  const [expandedCategories, setExpandedCategories] = useState<React.Key[]>([]);
  const { t } = useTranslation(["footprinting"]);

  const columns: ColumnConfiguration[] = [
    { title: null, dataIndex: "dot" },
    { title: t("resultsPage.table.columnHeader.source"), dataIndex: "source" },
    {
      title: (
        <Flex gap={4}>
          {t("resultsPage.table.columnHeader.scope")}
          <TooltipWrapper className="scope-info-icon-container" tooltip={{ title: t("resultsPage.table.columnHeader.scopeTooltip"), placement: "top" }}>
            <Icon className="scope-info-icon" iconName="tabler/info-circle" size="s" />
          </TooltipWrapper>
        </Flex>
      ),
      dataIndex: "scope",
    },
    { title: t("resultsPage.table.columnHeader.totalCO2e"), dataIndex: "totalCO2e" },
    { title: t("resultsPage.table.columnHeader.perHaCO2e"), dataIndex: "perHaCO2e" },
    { title: t("resultsPage.table.columnHeader.perKgYieldCO2e"), dataIndex: "perKgYieldCO2e", hidden: hidePerYield },
    { title: t("resultsPage.table.columnHeader.shareOfTotal"), dataIndex: "shareOfTotal" },
    { title: null, dataIndex: "expandIcon" },
  ];

  if (!footprintCalculationSummary) {
    return null;
  }

  const tableData = (Object.entries(footprintCalculationSummary?.tableData) as CalculationSummaryTuple[])
    .sort((a, b) => b[1].data.totalCO2e - a[1].data.totalCO2e)
    .flatMap(([category, row]): TableDataRow[] => {
      const expandable = row.subData.length > 0;
      const isExpanded = expandedCategories.includes(category);

      const categoryScope = FOOTPRINTING_SCOPE_MAPPING[category as FootprintingCategory];
      const hasSubCategories = typeof categoryScope === "object" && Object.keys(categoryScope).length;
      const allSameScope = hasSubCategories && new Set(row.subData.map((subData) => categoryScope[subData?.source])).size === 1;
      const scopeValue = hasSubCategories
        ? allSameScope
          ? t("resultsPage.table.rowValue.scope", {
              scope: categoryScope[row.subData?.[0]?.source],
            })
          : t("resultsPage.table.rowValue.mixed")
        : t("resultsPage.table.rowValue.scope", {
            scope: categoryScope,
          });

      const categoryRow: TableDataRow = {
        key: category,
        source: t(`categories.${category}`),
        scope: String(scopeValue),
        dot: <Icon iconName={"tabler/circle"} strokeWidth={"0px"} fill={FOOTPRINTING_CATEGORY_COLORS[category as FootprintingCategory]} />,
        totalCO2e: formatNumber({ number: trimToDecimals(row.data.totalCO2e, 2) }),
        perHaCO2e: formatNumber({ number: trimToDecimals(row.data.perHaCO2e, 2) }),
        perKgYieldCO2e: formatNumber({ number: trimToDecimals(row.data.perKgYieldCO2e, 2) }),
        shareOfTotal: formatNumber({ number: trimToDecimals(100 * row.data.shareOfTotal, 2) }),
        expandable,
        expandIcon: expandable ? <Icon iconName={`tabler/chevron-${isExpanded ? "up" : "down"}`} /> : undefined,
      };

      const expandedRows: TableDataRow[] = !isExpanded
        ? []
        : row.subData.map((subData): TableDataRow => {
            const subcategoryScope = hasSubCategories ? categoryScope[subData?.source as SubDataRowSource] : undefined;

            return {
              key: `${category}.${subData.source}`,
              source: t(`subCategories.${subData.source}`),
              scope: `${t("resultsPage.table.columnHeader.scope")} ${subcategoryScope}`,
              dot: null,
              expandable: false,
              totalCO2e: formatNumber({ number: trimToDecimals(subData.totalCO2e, 2) }),
              perHaCO2e: formatNumber({ number: trimToDecimals(subData.perHaCO2e, 2) }),
              perKgYieldCO2e: formatNumber({ number: trimToDecimals(subData.perKgYieldCO2e, 2) }),
              shareOfTotal: formatNumber({ number: trimToDecimals(subData.shareOfTotal, 2) }),
            };
          });

      return [categoryRow, ...expandedRows];
    });

  return (
    <div>
      <Table
        data={tableData}
        columns={columns}
        expandable={{
          expandedRowRender: () => <div className="results-table-hide-expanded-row" />,
          onExpandedRowsChange: (ks) => setExpandedCategories([...ks]),
        }}
      />
    </div>
  );
};
